import { create } from 'zustand';
import { z } from 'zod';

const courseSchema = z.object({
  courseName: z.string().min(1, 'Course name is required'),
  courseId: z.string().min(1, 'Course ID is required'),
  courseCode: z.string().optional(),
  deliveryType: z.string().optional(),
  learningHours: z.number().optional(),
  modeOfDelivery: z.string().optional(),
  // Optional fields for edit mode (to track existing records)
  trLCMSPlanningCoursesID: z.string().optional()
});

// Define target position schema for form
const targetPositionSchema = z.object({
  positionId: z.string().min(1, 'Position name is required'),
  positionName: z.string().min(1, 'Position name is required'),
  businessUnitIds: z.array(z.string()).optional(),
  jobFamilyGroupId: z.string().optional(),
  jobFamilyId: z.string().optional(),
  // Optional field for edit mode (to track existing records)
  trLCMSPlanningTargetPositionsID: z.string().optional()
});

// Define business unit schema for form
const businessUnitSchema = z.object({
  businessUnitId: z.string().min(1, 'Business Unit ID is required'),
  businessUnitName: z.string().min(1, 'Business Unit name is required'), // Changed from buName
  trLCMSPlanningBusinessUnitsID: z.string().optional()
});

const jobFamilyGroupSchema = z.object({
  jobFamilyGroupId: z.string().min(1, 'Job Family Group ID is required'),
  jobFamilyGroupName: z.string().min(1, 'Job Family Group name is required'),
  businessUnitIds: z.array(z.string()).optional(),
  trLCMSPlanningJobFamilyGroupsID: z.string().optional()
});

const jobFamilySchema = z.object({
  jobFamilyId: z.string().min(1, 'Job Family ID is required'),
  jobFamilyName: z.string().min(1, 'Job Family name is required'),
  jobFamilyGroupId: z.string().min(1, 'Job Family Group ID is required'),
  jobFamilyGroupName: z.string().min(1, 'Job Family Group name is required'),
  trLCMSPlanningJobFamiliesID: z.string().optional()
});

const coursePlanSchema = z.object({
  courseId: z.string().optional(),
  planName: z.string().min(1, 'Plan name is required'),
  programType: z.string().min(1, 'Program type is required'),
  description: z.string().optional(),
  courses: z.array(courseSchema).default([]),
  targetPositions: z.array(targetPositionSchema).default([]),
  businessUnits: z.array(businessUnitSchema).default([]),
  jobFamilyGroups: z.array(jobFamilyGroupSchema).default([]),
  jobFamilies: z.array(jobFamilySchema).default([]),
  targetPosition: z.string().optional(),
  startTime: z.string().min(1, 'Schedule start time is required'),
  endTime: z.string().min(1, 'Schedule end time is required'),
  notes: z.string().optional()
});

type CoursePlanFormData = z.infer<typeof coursePlanSchema>;

type Mode = 'add' | 'edit' | 'view';

interface CoursePlanState {
  formData: CoursePlanFormData;
  mode: Mode;
  isDataSet: boolean;
  setFormData: (_data: Partial<CoursePlanFormData>) => void;
  setFormMode: (_data: Mode) => void;
  reset: () => void;
}

const getInitialFormData = (): CoursePlanFormData => ({
  courseId: undefined,
  planName: '',
  programType: '',
  description: '',
  courses: [],
  targetPositions: [],
  businessUnits: [],
  jobFamilyGroups: [],
  jobFamilies: [],
  targetPosition: '',
  startTime: '',
  endTime: '',
  notes: ''
});

export const useCoursePlanStore = create<CoursePlanState>((set, get) => ({
  formData: getInitialFormData(),
  mode: 'add',
  isDataSet: false,
  setFormData: (data) => {
    const currentData = get().formData;
    // FIX: Only update if data actually changed
    const hasChanges = Object.keys(data).some((key) => {
      const newValue = data[key as keyof CoursePlanFormData];
      const currentValue = currentData[key as keyof CoursePlanFormData];
      return JSON.stringify(newValue) !== JSON.stringify(currentValue);
    });

    if (hasChanges) {
      set((state) => ({
        formData: { ...state.formData, ...data },
        isDataSet: true
      }));
    }
  },
  setFormMode: (data) => set(() => ({ mode: data })),
  reset: () =>
    set({
      formData: getInitialFormData(),
      mode: 'add',
      isDataSet: false
    })
}));

export { coursePlanSchema };
export type { CoursePlanFormData };
